import React, { useState, useRef, useEffect } from 'react';
import { Bot, Send, Sparkles, RefreshCw, Zap, Shield, Cpu, CornerDownLeft, AlertCircle } from 'lucide-react';
import { aiService } from '../../services/api';

const QUICK_PROMPTS = [
  { label: 'Phân tích SMC BTC/USDT', query: 'Phân tích cấu trúc thị trường BTC/USDT: Xác định vùng Order Block (OB), Fair Value Gap (FVG) và thanh khoản hai đầu.' },
  { label: 'Kịch bản Breakout ETH', query: 'Đánh giá kịch bản phá vỡ kháng cự ETH/USDT: Khối lượng giao dịch và tín hiệu xác nhận dòng tiền thông minh.' },
  { label: 'Đo lường thanh khoản SOL', query: 'Quét vùng thanh khoản quét râu (Liquidity Sweep) trên SOL/USDT khung 15m và 1h.' },
  { label: 'Chiến lược quản trị rủi ro', query: 'Gợi ý tỷ lệ R:R và điểm Stop Loss an toàn cho vị thế Long hiện tại.' }
];

export default function AICopilotChat() {
  const [messages, setMessages] = useState([
    {
      id: 'welcome',
      role: 'assistant',
      content: 'Chào mừng Operator! Tôi là **TITAN Market Copilot** — trợ lý phân tích thị trường tổ chức được bảo vệ qua hệ thống AI Gateway Reverse Proxy.\n\nHãy chọn kịch bản mẫu bên dưới hoặc nhập lệnh để phân tích biểu đồ, quét cấu trúc Smart Money Concepts (SMC) và tối ưu điểm vào lệnh.',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      provider: 'TITAN Core Gateway'
    }
  ]);
  const [inputQuery, setInputQuery] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeSymbol, setActiveSymbol] = useState('BTC/USDT');
  const [activeTimeframe, setActiveTimeframe] = useState('15m');
  const [activeProviderName, setActiveProviderName] = useState('Mock Sandbox (SMC Engine)');
  const [streamError, setStreamError] = useState(null);

  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isStreaming]);

  // Load public AI Hub status
  useEffect(() => {
    aiService.getHubStatus()
      .then(res => {
        if (res?.activeProvider) {
          setActiveProviderName(res.providers?.[res.activeProvider]?.name || res.activeProvider);
        }
      })
      .catch(() => {});
  }, []);

  const handleSend = async (customQuery) => {
    const query = (customQuery || inputQuery).trim();
    if (!query || isStreaming) return;

    setInputQuery('');
    setStreamError(null);

    const userMsgId = `user_${Date.now()}`;
    const assistantMsgId = `bot_${Date.now()}`;
    const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    const newMessages = [
      ...messages,
      { id: userMsgId, role: 'user', content: query, timestamp }
    ];

    setMessages([
      ...newMessages,
      { id: assistantMsgId, role: 'assistant', content: '', timestamp, isStreaming: true, provider: activeProviderName }
    ]);

    setIsStreaming(true);
    abortControllerRef.current = new AbortController();

    let fullResponse = '';

    await aiService.streamChat({
      query,
      symbol: activeSymbol,
      timeframe: activeTimeframe,
      messages: newMessages.map(m => ({ role: m.role, content: m.content })),
      signal: abortControllerRef.current.signal,
      onChunk: (chunk) => {
        if (chunk.type === 'token') {
          fullResponse += chunk.delta;
          setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, content: fullResponse } : m));
        } else if (chunk.type === 'session_ack') {
          setActiveProviderName(chunk.provider || activeProviderName);
        } else if (chunk.type === 'fallback_notice') {
          setStreamError(chunk.message);
        }
      },
      onDone: () => {
        setIsStreaming(false);
        setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, isStreaming: false } : m));
      },
      onError: (err) => {
        setIsStreaming(false);
        setStreamError(err.message || 'Lỗi kết nối đến AI Gateway.');
        setMessages(prev => prev.map(m => m.id === assistantMsgId ? { 
          ...m, 
          isStreaming: false, 
          content: fullResponse || `[Hệ thống]: Không thể hoàn tất truy vấn (${err.message}).` 
        } : m));
      }
    });
  };

  const handleStopStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-[680px] glass-panel rounded-2xl border border-cyan-500/30 overflow-hidden shadow-2xl relative">
      
      {/* 1. Header Toolbar */}
      <div className="px-5 py-3.5 border-b border-white/10 bg-slate-950/60 backdrop-blur-md flex flex-wrap items-center justify-between gap-3 z-10">
        <div className="flex items-center space-x-3">
          <div className="w-9 h-9 rounded-xl bg-cyan-500/10 border border-cyan-400/40 flex items-center justify-center text-cyan-400 shadow-[0_0_15px_rgba(0,240,255,0.2)]">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <h3 className="font-display font-bold text-sm text-white uppercase tracking-wider">AI Market Copilot</h3>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                PROXY SECURED
              </span>
            </div>
            <div className="text-[11px] font-mono text-slate-400 flex items-center gap-1.5 mt-0.5">
              <Cpu className="w-3 h-3 text-cyan-400" />
              <span>Gateway: <strong className="text-cyan-300">{activeProviderName}</strong></span>
            </div>
          </div>
        </div>

        {/* Pair & Timeframe Controls */}
        <div className="flex items-center space-x-2 text-xs font-mono">
          <select 
            value={activeSymbol} 
            onChange={(e) => setActiveSymbol(e.target.value)}
            className="bg-slate-900 border border-cyan-500/40 text-cyan-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-400"
          >
            <option value="BTC/USDT">BTC/USDT</option>
            <option value="ETH/USDT">ETH/USDT</option>
            <option value="SOL/USDT">SOL/USDT</option>
            <option value="BNB/USDT">BNB/USDT</option>
          </select>

          <select 
            value={activeTimeframe} 
            onChange={(e) => setActiveTimeframe(e.target.value)}
            className="bg-slate-900 border border-cyan-500/40 text-cyan-300 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-cyan-400"
          >
            <option value="1m">1m</option>
            <option value="5m">5m</option>
            <option value="15m">15m</option>
            <option value="1h">1h</option>
            <option value="4h">4h</option>
            <option value="1D">1D</option>
          </select>
        </div>
      </div>

      {/* Stream Warning Notice */}
      {streamError && (
        <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center gap-2 text-xs font-mono text-amber-300">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{streamError}</span>
          <button 
            type="button" 
            onClick={() => setStreamError(null)} 
            className="text-slate-400 hover:text-white underline cursor-pointer"
          >
            Đóng
          </button>
        </div>
      )}

      {/* 2. Message History Area */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 font-mono text-xs sm:text-sm custom-scrollbar">
        {messages.map((m) => {
          const isUser = m.role === 'user';
          return (
            <div 
              key={m.id} 
              className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} space-y-1`}
            >
              <div className="flex items-center space-x-2 text-[10px] text-slate-500 px-1">
                <span>{isUser ? 'OPERATOR' : 'TITAN COPILOT'}</span>
                <span>&bull;</span>
                <span>{m.timestamp}</span>
              </div>

              <div 
                className={`max-w-[85%] sm:max-w-[75%] p-4 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                  isUser 
                    ? 'bg-gradient-to-r from-cyan-600/30 to-blue-600/30 border border-cyan-400/40 text-cyan-100 rounded-br-sm shadow-[0_0_20px_rgba(0,240,255,0.1)]' 
                    : 'bg-slate-900/80 border border-white/10 text-slate-200 rounded-bl-sm shadow-xl'
                }`}
              >
                {m.content}
                {m.isStreaming && (
                  <span className="inline-block w-2 h-4 ml-1 bg-cyan-400 animate-pulse align-middle" />
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* 3. Quick SMC Prompts Chips */}
      <div className="px-4 py-2 border-t border-white/5 bg-slate-950/40 flex items-center space-x-2 overflow-x-auto no-scrollbar">
        <Sparkles className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
        {QUICK_PROMPTS.map((qp, idx) => (
          <button
            key={idx}
            type="button"
            disabled={isStreaming}
            onClick={() => handleSend(qp.query)}
            className="px-2.5 py-1 rounded-full text-[11px] font-mono bg-cyan-950/40 border border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20 hover:border-cyan-400 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
          >
            {qp.label}
          </button>
        ))}
      </div>

      {/* 4. Input Bar */}
      <div className="p-3 sm:p-4 border-t border-white/10 bg-slate-950/80 backdrop-blur-md">
        <form 
          onSubmit={(e) => {
            e.preventDefault();
            handleSend();
          }}
          className="relative flex items-center gap-2"
        >
          <input
            type="text"
            value={inputQuery}
            onChange={(e) => setInputQuery(e.target.value)}
            placeholder={`Hỏi AI Copilot về xu hướng ${activeSymbol} (${activeTimeframe})...`}
            disabled={isStreaming}
            className="w-full bg-slate-900/90 border border-white/15 focus:border-cyan-400 text-slate-100 placeholder-slate-500 rounded-xl px-4 py-3 text-xs sm:text-sm font-mono focus:outline-none focus:ring-1 focus:ring-cyan-400 transition-all pr-24"
          />

          {isStreaming ? (
            <button
              type="button"
              onClick={handleStopStream}
              className="absolute right-2 px-3 py-1.5 rounded-lg bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30 text-xs font-mono font-semibold transition-all cursor-pointer"
            >
              STOP
            </button>
          ) : (
            <button
              type="submit"
              disabled={!inputQuery.trim()}
              className="absolute right-2 px-3.5 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-500/30 text-xs font-mono font-bold flex items-center gap-1 transition-all disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              <span>GỬI</span>
              <CornerDownLeft className="w-3.5 h-3.5" />
            </button>
          )}
        </form>

        <div className="flex items-center justify-between mt-2 px-1 text-[10px] font-mono text-slate-500">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-emerald-400" />
            <span>Proxy Sanitization Active &bull; Zero API Key Leaks</span>
          </span>
          <span>Shift + Enter để xuống dòng</span>
        </div>
      </div>

    </div>
  );
}
